/**
 * Decides what a cmd-click in a PR diff opens: the path or name under the pointer, and for a name,
 * the line that defines it, in the file it is imported from or in a file that defines it. Pure
 * apart from the injected readers, so it runs without the DOM or network.
 */

import type { CodeContent } from '@/types/github';

import type { ImportBinding } from './import-bindings';
import type { LineRange } from './repo-file-resolution';
import type {
  PrV2RepoFilePeek,
  PrV2RepoFileRequest,
  PrV2RepoFileResult,
  PrV2RepoFileSide,
} from './types';

import { findDefaultExportLine, findImportBinding, isProseAt } from './import-bindings';
import {
  findRepoFileSpecifierRange,
  parseRepoFileSpecifier,
  splitRepoFilePath,
} from './repo-file-resolution';

import {
  findSymbolDefinitionInFiles,
  findSymbolDefinitionLine,
} from '@/lib/symbols/find-symbol-definition';

/**
 * What a cmd-click at a point opens: a path as written, or the definition of a name. A name the
 * click is known to open, because a relative or aliased import brings it in or its file defines
 * it, is a linked name, and hovering it looks like hovering the path.
 */
export type PrV2LinkTarget = { kind: 'path' | 'linked-name' | 'name'; range: LineRange };

/** A point on a diff line, and the full text of the file the line is from. */
export type PrV2LinkPoint = {
  /** The line's text. */
  lineText: string;
  offset: number;
  fromPath: string;
  side: PrV2RepoFileSide;
  /** The full text of `fromPath` on `side`, or null while the page does not hold it. */
  fileText: string | null;
};

export type PrV2SymbolPeekResolution =
  | { kind: 'peek'; peek: PrV2RepoFilePeek }
  /** The name is imported from a package, which has no file in the repository to open. */
  | { kind: 'external'; specifier: string }
  | { kind: 'not-found' };

export type PrV2SymbolPeekChangedFile = Pick<
  CodeContent,
  'filename' | 'previous_filename' | 'oldContent' | 'newContent'
>;

/** The clicked point, with the name under it; the point's file text saves reading the file. */
export type PrV2SymbolPeekInput = Omit<PrV2LinkPoint, 'offset'> & {
  name: string;
  /** Reads a repository file at the comparison the diff shows; rejects when the read fails. */
  readFile: (request: PrV2RepoFileRequest) => Promise<PrV2RepoFileResult>;
  /** The pull request's changed files; missing contents are skipped unless the loader recovers them. */
  changedFiles: readonly PrV2SymbolPeekChangedFile[];
  /** Loads changed-file contents only after imports and the clicked file have no match. */
  loadChangedFiles?: () => Promise<readonly PrV2SymbolPeekChangedFile[]>;
};

type NameQuery = Pick<PrV2SymbolPeekInput, 'name' | 'lineText' | 'fromPath' | 'side'>;

/** Where a cmd-click on a name goes, before any file an import names is read. */
type NameTarget =
  | { kind: 'import'; binding: ImportBinding }
  | { kind: 'definition'; peek: PrV2RepoFilePeek };

const IDENTIFIER_CHARACTER = /[\p{L}\p{N}_$]/u;
const DIGIT = /\p{N}/u;

/**
 * The link at a point, or null off one. A path under the point wins over the name it may contain,
 * as in `'./use-foo'`. A name is linked when the click's lookup, on the clicked line and the file
 * text in hand, opens a file. It stops before the other changed files, because searching every one
 * on each pointer move costs too much, so a name only they define underlines once the modifier is
 * held. A word in a comment or string is prose and never linked, though a held click still looks
 * it up.
 */
export function findPrV2LinkTarget({
  lineText,
  offset,
  fromPath,
  side,
  fileText,
}: PrV2LinkPoint): PrV2LinkTarget | null {
  const path = findRepoFileSpecifierRange(lineText, offset, fromPath);

  if (path) {
    return { kind: 'path', range: path };
  }

  const name = findIdentifierRangeAt(lineText, offset);

  if (!name) {
    return null;
  }

  if (isProseAt(lineText, offset, fromPath)) {
    return { kind: 'name', range: name };
  }

  const query = { name: lineText.slice(name.start, name.end), lineText, fromPath, side };
  const target =
    findLineTarget(query) ?? (fileText === null ? null : findFileTarget(query, fileText));

  return { kind: opensFile(target, fromPath) ? 'linked-name' : 'name', range: name };
}

/** The identifier at `offset` in a line, or null off one or on a number. */
function findIdentifierRangeAt(lineText: string, offset: number): LineRange | null {
  if (!IDENTIFIER_CHARACTER.test(lineText[offset] ?? '')) {
    return null;
  }

  let start = offset;
  let end = offset + 1;

  while (start > 0 && IDENTIFIER_CHARACTER.test(lineText[start - 1] ?? '')) {
    start -= 1;
  }

  while (end < lineText.length && IDENTIFIER_CHARACTER.test(lineText[end] ?? '')) {
    end += 1;
  }

  return DIGIT.test(lineText[start] ?? '') ? null : { start, end };
}

/**
 * In order: an import of the name on the clicked line, an import of it anywhere in the clicked
 * file (the diff usually hides the import block), a definition in the clicked file, then a
 * definition in another changed file, test files last. A package import stops the search, because
 * a same-named definition elsewhere is not the one the code uses. Each step reads only when the
 * steps before it find nothing: the clicked file when the page lacks its text, then the changed
 * files' missing contents.
 */
export async function resolvePrV2SymbolPeek(
  input: PrV2SymbolPeekInput,
): Promise<PrV2SymbolPeekResolution> {
  const { fromPath, side, fileText, readFile, loadChangedFiles } = input;
  const target =
    findLineTarget(input) ??
    findFileTarget(input, fileText ?? readText(await readFile(requestRepoPath(fromPath, side)))) ??
    findChangedFileTarget(input, loadChangedFiles ? await loadChangedFiles() : input.changedFiles);

  if (!target) {
    return { kind: 'not-found' };
  }

  return target.kind === 'import'
    ? peekImport(target.binding, input)
    : { kind: 'peek', peek: target.peek };
}

function findLineTarget({ name, lineText, fromPath }: NameQuery): NameTarget | null {
  const binding = findImportBinding(lineText, name, fromPath);

  return binding ? { kind: 'import', binding } : null;
}

function findFileTarget({ name, fromPath, side }: NameQuery, fileText: string): NameTarget | null {
  const binding = findImportBinding(fileText, name, fromPath);

  if (binding) {
    return { kind: 'import', binding };
  }

  const line = findSymbolDefinitionLine(fileText, name);

  return line === null
    ? null
    : { kind: 'definition', peek: { file: requestRepoPath(fromPath, side), line } };
}

function findChangedFileTarget(
  { name, fromPath }: NameQuery,
  changedFiles: readonly PrV2SymbolPeekChangedFile[],
): NameTarget | null {
  const match = findSymbolDefinitionInFiles(
    name,
    changedFiles.filter(
      (file) => file.filename !== fromPath && file.previous_filename !== fromPath,
    ),
  );

  if (!match) {
    return null;
  }

  const file =
    match.side === 'new'
      ? requestRepoPath(match.file.filename, 'head')
      : requestRepoPath(match.file.previous_filename ?? match.file.filename, 'base');

  return { kind: 'definition', peek: { file, line: match.lineNumber } };
}

/**
 * A click opens a definition, or a relative or aliased import, whose module the panel shows even
 * when the repository lacks it. A bare path such as `next/server`, or Python's `app/utils.py`, may
 * name a package, whose click only says where the name comes from, so it is not known to open.
 */
function opensFile(target: NameTarget | null, fromPath: string): boolean {
  if (target?.kind !== 'import') {
    return target?.kind === 'definition';
  }

  const specifier = parseRepoFileSpecifier(target.binding.specifier, fromPath);

  return specifier?.kind === 'relative' || specifier?.kind === 'alias';
}

/**
 * The imported module at the imported name's definition, or at its top when the module does not
 * define the name itself. A bare path such as `next/server` names a package subpath as often as a
 * repository file, so one the repository lacks counts as external.
 */
async function peekImport(
  binding: ImportBinding,
  { fromPath, side, readFile }: PrV2SymbolPeekInput,
): Promise<PrV2SymbolPeekResolution> {
  const specifier = parseRepoFileSpecifier(binding.specifier, fromPath);

  if (specifier === null || specifier.kind === 'package') {
    return { kind: 'external', specifier: binding.specifier };
  }

  const file: PrV2RepoFileRequest = { specifier: binding.specifier, fromPath, side };
  const result = await readFile(file);

  if (
    result.status === 'not-found' &&
    result.reason === 'no-match' &&
    specifier.kind === 'bare-path'
  ) {
    return { kind: 'external', specifier: binding.specifier };
  }

  const line =
    binding.importedName === null
      ? null
      : binding.importedName === 'default'
        ? findDefaultExportLine(
            readText(result),
            result.status === 'found' ? result.path : file.specifier,
          )
        : findSymbolDefinitionLine(readText(result), binding.importedName);

  return { kind: 'peek', peek: { file, line } };
}

/**
 * Names `path` exactly: its file name read from the file itself. A bare path would not do, because
 * one without a slash, such as `go.mod`, parses as a package.
 */
function requestRepoPath(path: string, side: PrV2RepoFileSide): PrV2RepoFileRequest {
  return { specifier: `./${splitRepoFilePath(path).name}`, fromPath: path, side };
}

/** A file the panel cannot show as text has nothing to search. */
function readText(result: PrV2RepoFileResult): string {
  return result.status === 'found' ? result.contents : '';
}
